import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { formatApiError } from "../lib/api";
import { Home, Phone, Lock } from "lucide-react";
import { toast } from "sonner";
import GoogleSignInButton from "../components/GoogleSignInButton";

export default function Login() {
  const { t } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!phone || !password) return setError(t("fields_required"));
    setLoading(true);
    try {
      const u = await login(phone, password);
      // Admin accounts go straight to the admin panel — they can't use the site as a regular user
      navigate(u?.role === "admin" ? "/admin" : "/app");
    } catch (err) {
      setError(formatApiError(err));
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grain-bg flex flex-col">
      <header className="max-w-7xl w-full mx-auto flex items-center justify-between px-4 py-4">
        <Link to="/" className="flex items-center gap-2 font-semibold text-[#2A5948]">
          <Home size={18} />
          <span>{t("brand")}</span>
        </Link>
        <LanguageSwitcher />
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md bg-white border border-[#E5E1D8] rounded-2xl p-8 shadow-sm">
          <h1 className="text-2xl font-semibold">{t("login_title")}</h1>
          <p className="text-sm text-[#5C6053] mt-1">{t("login_sub")}</p>

          <form className="mt-6 space-y-4" onSubmit={onSubmit}>
            <Field label={t("phone")} icon={Phone}>
              <input
                data-testid="login-phone-input"
                type="tel"
                dir="ltr"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full bg-transparent outline-none text-sm"
                placeholder="+9689XXXXXXXX"
              />
            </Field>
            <Field label={t("password")} icon={Lock}>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-transparent outline-none text-sm"
              />
            </Field>

            {error && (
              <div data-testid="login-error" className="text-sm text-[#C96A52] bg-[#FBE9E4] border border-[#F1C9BF] rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full brand-btn py-3 rounded-xl font-medium disabled:opacity-60"
            >
              {loading ? t("signing_in") : t("login")}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-[#5C6053]">
            <div className="h-px flex-1 bg-[#E5E1D8]" />
            <span>{t("or")}</span>
            <div className="h-px flex-1 bg-[#E5E1D8]" />
          </div>

          <GoogleSignInButton testid="login-google-btn" />

          <div className="mt-6 text-sm text-center text-[#5C6053]">
            <Link data-testid="link-to-forgot" to="/forgot-password" className="text-[#2A5948] font-medium">
              {t("forgot_password_link")}
            </Link>
          </div>
          <div className="mt-3 text-sm text-center text-[#5C6053]">
            {t("dont_have_account")}{" "}
            <Link data-testid="link-to-register" to="/register" className="text-[#2A5948] font-medium">
              {t("register")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-[#5C6053] mb-1">{label}</div>
      <div className="flex items-center gap-2 border border-[#E5E1D8] bg-[#F9F6F0] rounded-xl px-3 py-2.5 focus-within:ring-2 focus-within:ring-[#2A5948]/40">
        {Icon && <Icon size={16} className="text-[#5C6053]" />}
        {children}
      </div>
    </label>
  );
}
